import * as XLSX from "xlsx";
import { findMatchingHeader, getDefaultMopSchema, normalizeHeader } from "./posRecoColumnSchema";

const toHeaderList = (row = []) =>
	(row || [])
		.map((cell) => String(cell ?? "").trim())
		.filter((cell) => cell && !["nan", "null", "undefined"].includes(cell.toLowerCase()));

const normalizeSheetName = (name) => String(name || "").trim().toLowerCase();

const getSheet = (workbook, sheetName) => {
	const names = workbook?.SheetNames || [];
	if (sheetName) {
		if (workbook.Sheets?.[sheetName]) return workbook.Sheets[sheetName];
		const target = normalizeSheetName(sheetName);
		const match = names.find((name) => normalizeSheetName(name) === target);
		return match ? workbook.Sheets[match] : null;
	}
	const fallback = names[0];
	return fallback ? workbook.Sheets[fallback] : null;
};

const resolveReadConfig = (mopConfig = {}) => {
	const defaults = getDefaultMopSchema(mopConfig.id) || {};
	return {
		...defaults,
		...mopConfig,
		headerMode: mopConfig.headerMode || defaults.headerMode || "first_row",
		headerRow: Number.isInteger(mopConfig.headerRow) ? mopConfig.headerRow : defaults.headerRow,
		sheetName: mopConfig.sheetName || defaults.sheetName || null,
		marker: mopConfig.marker || defaults.marker || null,
	};
};

const sheetToRows = (sheet, maxRows = 80) =>
	XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", blankrows: false, raw: false }).slice(0, maxRows);

const cellText = (cell) => {
	if (!cell) return "";
	const value = cell.w != null && String(cell.w) !== "" ? cell.w : cell.v;
	return String(value ?? "").trim();
};

/**
 * Rows by Excel index, including blank preamble rows.
 * Matches pandas read_excel(skiprows=N): the header is the 0-based row N.
 * sheet_to_json({ blankrows: false }) drops those blanks and shifts the index.
 */
const sheetToPhysicalRows = (sheet, maxRows = 120) => {
	const ref = sheet?.["!ref"];
	if (!ref) return [];
	const range = XLSX.utils.decode_range(ref);
	const lastRow = Math.min(Math.max(range.e.r, 0), Math.max(maxRows, 1) - 1);
	const rows = [];
	for (let rowIndex = 0; rowIndex <= lastRow; rowIndex += 1) {
		const row = [];
		for (let colIndex = range.s.c; colIndex <= range.e.c; colIndex += 1) {
			row.push(cellText(sheet[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })]));
		}
		rows.push(row);
	}
	return rows;
};

const rowContainsHeaders = (row, requiredHeaders = []) => {
	const normalizedRow = new Set(toHeaderList(row).map((header) => normalizeHeader(header)));
	return requiredHeaders
		.filter(Boolean)
		.every((header) => normalizedRow.has(normalizeHeader(header)));
};

export const readPosRecoFileHeaders = (file, mopConfig = {}, mappingColumns = []) =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = (event) => {
			try {
				const data = new Uint8Array(event.target.result);
				const readConfig = resolveReadConfig(mopConfig);
				const headerMode = readConfig.headerMode || "first_row";
				const headerRow = Number.isInteger(readConfig.headerRow) ? readConfig.headerRow : 0;
				const workbook = XLSX.read(data, {
					type: "array",
					sheetRows: headerMode === "row_index" ? Math.max(120, headerRow + 1) : 120,
				});
				const sheet = getSheet(workbook, readConfig.sheetName);
				if (!sheet) {
					resolve([]);
					return;
				}

				if (headerMode === "row_index") {
					// Count blank preamble rows, same as pandas.
					// Bank statement: skiprows=20. Zomato: header=6 on "Transactions summary".
					// The separator row under that header is data, not the header.
					const rows = sheetToPhysicalRows(sheet, Math.max(120, headerRow + 1));
					resolve(toHeaderList(rows[headerRow] || []));
					return;
				}

				const rows = sheetToRows(sheet, 120);

				if (headerMode === "after_marker") {
					const marker = String(mopConfig.marker || "").toLowerCase();
					const markerIndex = rows.findIndex((row) =>
						(row || []).some((cell) => String(cell).toLowerCase().includes(marker)),
					);
					if (markerIndex >= 0) {
						resolve(toHeaderList(rows[markerIndex + 1] || []));
						return;
					}
					resolve(toHeaderList(rows[0] || []));
					return;
				}

				if (headerMode === "match_mapped") {
					const requiredHeaders = (mappingColumns || [])
						.filter((column) => column.is_required && column.file_header)
						.map((column) => column.file_header);
					const matchIndex = rows.findIndex((row) => rowContainsHeaders(row, requiredHeaders));
					if (matchIndex >= 0) {
						resolve(toHeaderList(rows[matchIndex] || []));
						return;
					}
					const firstNonEmpty = rows.find((row) => toHeaderList(row).length > 0);
					resolve(toHeaderList(firstNonEmpty || []));
					return;
				}

				resolve(toHeaderList(rows[0] || []));
			} catch (err) {
				reject(err);
			}
		};
		reader.onerror = () => reject(reader.error || new Error("Failed to read file"));
		reader.readAsArrayBuffer(file);
	});

export const autoMatchColumnMap = (columns = [], fileHeaders = []) => {
	const next = {};
	(columns || []).forEach((column) => {
		const matched =
			findMatchingHeader(fileHeaders, column.file_header) ||
			findMatchingHeader(fileHeaders, column.internal_column) ||
			"";
		next[column.internal_column] = matched;
	});
	return next;
};
